/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { isEqual } from 'lodash'

export interface PermissionsFormOptions<T> {
  /** The last saved state; null until it has loaded. */
  baseline: T | null
  /** Order-insensitive form of a value, so reordering never counts as a change. */
  normalise: (value: T) => unknown
}

export interface PermissionsForm<T> {
  value: T
  setValue: (update: T | ((previous: T) => T)) => void
  isDirty: boolean
  /** Cancel: back to the last saved state. */
  reset: () => void
  /** After a successful save: the value becomes the new baseline. */
  commit: (saved: T) => void
}

/**
 * Save/Cancel form state over a baseline that may arrive later (async load). The
 * baseline seeds the value once; a later baseline change (a reload) reseeds it.
 */
export function usePermissionsForm<T extends object>({ baseline, normalise }: PermissionsFormOptions<T>) {
  const [saved, setSaved] = useState<T | null>(baseline)
  const [value, setValueState] = useState<T | null>(baseline)
  const seeded = useRef<T | null>(baseline)

  useEffect(() => {
    if (baseline !== null && baseline !== seeded.current) {
      seeded.current = baseline
      setSaved(baseline)
      setValueState(baseline)
    }
  }, [baseline])

  const setValue = useCallback((update: T | ((previous: T) => T)) => {
    setValueState((previous) => {
      if (previous === null) return previous
      return typeof update === 'function' ? (update as (p: T) => T)(previous) : update
    })
  }, [])

  const reset = useCallback(() => {
    setValueState(saved)
  }, [saved])

  const commit = useCallback((next: T) => {
    setSaved(next)
    setValueState(next)
  }, [])

  const isDirty = useMemo(
    () => saved !== null && value !== null && !isEqual(normalise(value), normalise(saved)),
    [saved, value, normalise]
  )

  return { value, setValue, isDirty, reset, commit } as PermissionsForm<T> & { value: T | null }
}
